/**
 * POST-initiated server-sent event streams. EventSource is GET-only and cannot
 * carry an Authorization header, so frames are read straight off a fetch body.
 */

import { API_BASE_URL } from '@/config/api';
import { clearAuthAndRedirect, getAuthHeaders, refreshTokens } from '@/lib/api';

/**
 * A stream the server refused, or ended with its `error` event. `errors` is the ApiError's own detail
 * when it sent one, such as the datasets that block a write; absent otherwise.
 */
export class StreamFailure extends Error {
	readonly errors: readonly unknown[] | undefined;

	constructor(message: string, errors?: unknown) {
		super(message);
		this.name = 'StreamFailure';
		this.errors = Array.isArray(errors) ? errors : undefined;
	}
}

interface PostEventStreamOptions {
	onEvent: (event: string, data: unknown) => void;
	signal?: AbortSignal;
}

const openStream = async (path: string, body: unknown, signal?: AbortSignal): Promise<Response> =>
	fetch(`${API_BASE_URL}${path}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
		body: JSON.stringify(body),
		signal
	});

export async function postEventStream(
	path: string,
	body: unknown,
	{ onEvent, signal }: PostEventStreamOptions
): Promise<void> {
	let response = await openStream(path, body, signal);
	// getAuthHeaders only refreshes a clock-expired token, so a token rejected
	// while still valid-looking lands here. Refresh once and retry, as the axios
	// interceptor does for every other call, and log out if that refresh fails.
	if (response.status === 401) {
		try {
			await refreshTokens();
		} catch {
			clearAuthAndRedirect();
			throw new Error('Session expired');
		}
		response = await openStream(path, body, signal);
	}
	if (!response.ok || !response.body) {
		// A refusal before the stream opens is an ApiError body; anything else keeps the status line.
		const body = (await response.json().catch(() => null)) as { message?: unknown; errors?: unknown } | null;
		throw new StreamFailure(
			typeof body?.message === 'string' ? body.message : `Stream request failed with status ${response.status}`,
			body?.errors
		);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });

		const frames = buffer.split('\n\n');
		buffer = frames.pop() ?? '';

		for (const frame of frames) {
			let event = 'message';
			const dataLines: string[] = [];
			for (const line of frame.split('\n')) {
				if (line.startsWith('event: ')) event = line.slice(7);
				else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
			}
			if (dataLines.length) onEvent(event, JSON.parse(dataLines.join('\n')));
		}
	}
}
